import React, { useState, useEffect, useRef } from "react";
import { Outlet, useLocation } from "react-router-dom";
import { useDispatch } from "react-redux";
import { getPageProperties } from "../../routes/routes";
import { getTeams } from "../../redux/teams/actions";

import {
  UseUserDetails,
  UseIsAdmin,
  UseIsAffiliate,
  UseIsPrime,
  UseIsDemo,
  UseAccess,
} from "../../hooks/auth";
import {
  UseCompany,
  UseCompanyLogo,
  UseCompanyName,
  UseCompanyLoading,
  UseIsCompanyUpToDate,
  UseSelectedWorkspace,
  UseCompanySubWorkspaces,
  UseSubscriptionLevel,
} from "../../hooks/company";
import { UseUnclaimedCount } from "../../hooks/claims";
import { useKpiData } from "../../hooks/kpi";
import useLockBodyScroll from "../../hooks/useLockBodyScroll";

import { Sidebar, TopBar, getActiveKeyFromPathname } from "../../components/AppLayout";
import BottomNav from "../../components/BottomNav";
import Blackout from "../../components/Blackout";
import WorkspacePopover from "../../components/WorkspaceDrawer/WorkspacePopover";
import NavProfile from "../../components/ProfilePictures/NavProfile";
import LoadingScreen from "../../components/LoadingScreen";
import SoftBox from "../../components/SoftBox";
import SoftTypography from "../../components/SoftTypography";

import Box from "@mui/material/Box";
import useMediaQuery from "@mui/material/useMediaQuery";

import adminRoutes from "../../routes/adminNavRoutes";
import playerRoutes from "../../routes/playerNavRoutes";

import gmbNavbarLogo from "../../img/gmb-nav-round.png";

import UpgradeModal from "../../components/Billing/UpgradeModal";
import ArchivedModal from "../../components/Billing/ArchivedModal";
import PhoneGate from "../../components/PhoneNumberPrompt/PhoneGate";

function Main() {
  const { image, firstName, lastName, isAdmin: isTrueAdmin, isArchived } = UseUserDetails();
  const isLoading = UseCompanyLoading();
  const companyLoaded = UseIsCompanyUpToDate();
  const companyDetails = UseCompany();
  const isAdmin = UseIsAdmin();
  const isPrime = UseIsPrime();
  const isDemo = UseIsDemo();
  const isAffiliate = UseIsAffiliate();
  const logo = UseCompanyLogo();
  const company = UseCompanyName();
  const workspace = UseSelectedWorkspace();
  const subworkspaces = UseCompanySubWorkspaces();
  const isSubscribed = UseSubscriptionLevel();
  const access = UseAccess();
  const location = useLocation();
  const [badges, setBadges] = useState({});
  const isMobileView = useMediaQuery("(max-width:767px)");
  const [sidebarMobileOpen, setSidebarMobileOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    try {
      const stored = localStorage.getItem("sidebarCollapsed");
      return stored !== null ? JSON.parse(stored) : false;
    } catch {
      return false;
    }
  });
  const navRoutes = isAdmin ? adminRoutes : playerRoutes;
  const activePath = getActiveKeyFromPathname(location.pathname, navRoutes);
  const unclaimed = UseUnclaimedCount();
  const dispatch = useDispatch();

  const containerChildren = workspace?._id
    ? (subworkspaces[workspace._id] || subworkspaces[String(workspace._id)] || [])
    : [];
  // Company Metrics (Analytics > Metrics / Dashboards) is company-wide, not
  // scoped to the selected workspace — it aggregates every workspace's data
  // itself, so a group/container workspace has nothing to block it on.
  const isContainerWorkspace = !!workspace?.isContainer && activePath !== "company-metrics";

  // Drive the KPI fetch from the always-mounted shell. useKpiData fetches on mount
  // and re-fetches whenever companyId / selected workspace change (e.g. after persist
  // rehydration or the company â†’ workspace chain resolves), so Scorekeeper, the
  // leaderboard, King of the Mountain and the home dashboard always have data without
  // depending on the one-time Blackout intro being shown.
  useKpiData();

  // Hydrate the teams slice once on shell mount so Player Management and the
  // TV leaderboard both have data on first render. Saga is idempotent.
  useEffect(() => {
    dispatch(getTeams());
  }, [dispatch]);
  const [isUpgradeRequired, setIsUpgradeRequired] = useState(false);
  const [pageProps, setPageProps] = useState(null);

  // Track if this is the first mount in this browser session
  const [showBlackout, setShowBlackout] = useState(false);
  const hasRunBlackout = useRef(false);
  const prevWorkspaceId = useRef();

  useEffect(() => {
    if (!hasRunBlackout.current) {
      const routeProps = getPageProperties({ path: location.pathname });
      const skipBlackout = routeProps?.hideSideNav && routeProps?.hideTopbar;
      // Only run Blackout if not reloaded/reopened in this session
      // Use sessionStorage to persist across reloads in the same tab
      if (!skipBlackout && !window.sessionStorage.getItem("hasRunBlackout")) {
        setShowBlackout(true);
        window.sessionStorage.setItem("hasRunBlackout", "true");
      } else {
        setShowBlackout(false);
      }
      hasRunBlackout.current = true;
      prevWorkspaceId.current = workspace?._id;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // Show Blackout if workspace changes (but not on first mount if sessionStorage is set)
    if (hasRunBlackout.current) {
      const workspaceId = workspace?._id;
      if (
        workspaceId &&
        prevWorkspaceId.current &&
        String(workspaceId) !== String(prevWorkspaceId.current)
      ) {
        setShowBlackout(true);
      }
      prevWorkspaceId.current = workspaceId;
    }
  }, [workspace?._id]);

  useEffect(() => {
    if (unclaimed >= 0) {
      setBadges((prevValue) => ({ ...prevValue, claims: unclaimed }));
    }
  }, [unclaimed]);

  useEffect(() => {
    try {
      localStorage.setItem("sidebarCollapsed", JSON.stringify(sidebarCollapsed));
    } catch {
      // ignore
    }
  }, [sidebarCollapsed]);

  useEffect(() => {
    if (!isMobileView) setSidebarMobileOpen(false);
  }, [isMobileView]);

  // The mobile sidebar is a fixed overlay drawn on top of the page, but the
  // page underneath was still scrollable — once the drawer's own scroll
  // region hit its top/bottom, the leftover scroll delta chained through to
  // the body, dragging the background along with it. Lock scroll for as
  // long as the drawer is open so scrolling inside it stays contained.
  useLockBodyScroll(isMobileView && sidebarMobileOpen);

  useEffect(() => {
    // Workaround to fetch page properties; props aren't easily accessible on Main layout since props data belongs to its child component
    if (location.pathname) {
      const props = getPageProperties({ path: location.pathname });
      // Only lock the app when we positively know this company is unpaid.
      // Missing/failed company.subscription used to read as level 0 and false-trigger
      // "Your Trial Period Has Ended" for paid orgs (e.g. Johnny Tsunami).
      const sub = companyDetails?.subscription;
      const knownUnpaid =
        companyLoaded &&
        sub != null &&
        !(Number(sub.level) > 0);
      const upgradeFlag = !props?.isFreemium && knownUnpaid;
      setIsUpgradeRequired(upgradeFlag);
      setPageProps(props);
    }
  }, [location.pathname, isDemo, isSubscribed, companyLoaded, companyDetails?.subscription]);

  // Hide Blackout after it finishes (Blackout component should call setShowBlackout(false) when done)
  // We'll pass a prop to Blackout to allow it to close itself
  const handleBlackoutClose = () => setShowBlackout(false);

  const isFullscreenRoute = Boolean(pageProps?.hideSideNav && pageProps?.hideTopbar);

  // Real width the sidebar reserves in the flex layout, so TopBar (fixed,
  // positioned independently of flow) can start exactly where it ends.
  let sidebarOffset = 0;
  if (!pageProps?.hideSideNav && !isMobileView) {
    sidebarOffset = sidebarCollapsed ? 84 : 224;
  }

  let mainBody = <Outlet />;
  if (isLoading && !isFullscreenRoute) {
    mainBody = <LoadingScreen />;
  } else if (isContainerWorkspace && !isFullscreenRoute) {
    mainBody = (
      <SoftBox
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          minHeight: { mini: "calc(100dvh - 56px)", md: "70vh" },
          px: 3,
          textAlign: "center",
        }}
      >
        <SoftTypography
          sx={{
            fontSize: "20px",
            fontWeight: 600,
            color: "#101828",
            letterSpacing: "-0.02em",
          }}
        >
          {workspace?.name || "Sales"} is a group
        </SoftTypography>
        <SoftTypography
          sx={{
            fontSize: "14px",
            fontWeight: 500,
            color: "#667085",
            mt: "8px",
            maxWidth: 420,
            lineHeight: 1.5,
          }}
        >
          This workspace has no scores of its own. Open the workspace picker and choose a team underneath
          {containerChildren.length
            ? ` — ${containerChildren.map((c) => c.name).filter(Boolean).join(", ")}.`
            : " once subworkspaces are added."}
        </SoftTypography>
      </SoftBox>
    );
  }

  return (
    <>
      {/* Main Page Component */}
      <Box
        sx={{
          display: "flex",
          pointerEvents: isUpgradeRequired ? "none" : "auto",
        }}
      >
        {!pageProps?.hideSideNav ? (
          <>
            {isMobileView && sidebarMobileOpen && (
              <div
                className="fixed inset-0 bg-black/40 z-[9998] md:hidden"
                role="button"
                tabIndex={0}
                onClick={() => setSidebarMobileOpen(false)}
                onKeyDown={(e) => e.key === "Escape" && setSidebarMobileOpen(false)}
                aria-label="Close sidebar"
              />
            )}
            <Sidebar
              isCollapsed={sidebarCollapsed}
              toggleCollapse={() => setSidebarCollapsed((c) => !c)}
              isMobileOpen={sidebarMobileOpen}
              isMobileView={isMobileView}
              onMobileClose={() => setSidebarMobileOpen(false)}
              onNavigate={() => {}}
              activePath={activePath}
              routes={navRoutes}
              badges={badges}
              brandLogo={logo ?? gmbNavbarLogo}
              brandName={company || "Game My Biz"}
              subheader={workspace?.name}
              profileSlot={
                <NavProfile
                  image={image}
                  firstName={firstName}
                  lastName={lastName}
                  isAdmin={isAdmin}
                />
              }
              headerMenuSlot={isTrueAdmin ? <WorkspacePopover /> : null}
              subscriptionLevel={isSubscribed}
              access={access}
            />
          </>
        ) : null}

        {!pageProps?.hideTopbar ? (
          <TopBar
            activeWorkspaceName={workspace?.name}
            isGlobalView={activePath === "company-metrics" || activePath === "company-view"}
            onOpenSidebar={isMobileView ? () => setSidebarMobileOpen(true) : undefined}
            // The sidebar is an in-flow column on desktop (84px collapsed /
            // 224px expanded) but a fixed off-canvas overlay on mobile, so
            // it reserves no flex width there. TopBar is `position:fixed`
            // and previously always spanned from x:0, only staying clear
            // of the sidebar because its z-index lost to the sidebar's â€”
            // any animation/z-index edge case let sidebar content show
            // through. Passing the real reserved width lets TopBar start
            // exactly where the sidebar ends instead of relying on stacking
            // order to mask the overlap.
            sidebarOffset={sidebarOffset}
          />
        ) : null}
        <Box
          className="flex flex-col grow relative max-w-[100vw]"
          // overflowX "auto" — wide page content (e.g. Scorekeeper) scrolls
          // horizontally inside THIS Box instead of the document, so the
          // scroll gesture never reaches <html>/<body>. TopBar renders
          // OUTSIDE this Box entirely (as a sibling above), not nested
          // inside it, so it never shares a scroll container with this
          // content in the first place. paddingTop reserves the 56px the
          // TopBar claims from flow now that its spacer no longer sits above
          // this column.
          sx={{
            background: "#F4F4F4",
            overflowX: "auto",
            paddingTop: pageProps?.hideTopbar ? 0 : "56px",
          }}
        >
          <Box
            className="grow"
            sx={{
              // The top bar already occupies 56px. Giving the content another
              // full viewport on mobile created 56px of guaranteed blank
              // scrolling at the bottom of every page.
              minHeight: {
                mini: pageProps?.hideTopbar ? "100dvh" : "calc(100dvh - 56px)",
                md: "100vh",
              },
            }}
          >
            {mainBody}
          </Box>
        </Box>
        {showBlackout && !isFullscreenRoute && <Blackout onClose={handleBlackoutClose} />}
        <BottomNav badges={badges} />
        {/* <Box sx={{ display: { xs: "none", md: "block" } }}>
        <TextFeedback />
      </Box> */}
      </Box>

      {/* Upgrade overlay that blocks the Main component */}
      {!isLoading && isUpgradeRequired && <UpgradeModal />}
      {!isLoading && isArchived && <ArchivedModal />}

      {/* Require a phone number on file (TMAR-613). Yields to the archived /
          upgrade modals and never shows on fullscreen/kiosk routes. */}
      {!isLoading &&
        !isUpgradeRequired &&
        !isArchived &&
        !isFullscreenRoute && <PhoneGate />}
    </>
  );
}

export default Main;
